import React, { useState } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Search, Menu, Home, X, Facebook, Instagram, MessageCircle } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from './store/store';
import { useSearch } from './context/SearchContext';
import SearchModal from './components/SearchModal';
import FeaturedProducts from './components/FeaturedProducts';
import Men from './pages/Men';
import Women from './pages/Women';
import CargoPants from './pages/CargoPants';
import Sale from './pages/Sale';
import SearchResults from './pages/SearchResults';
import Shop from './pages/Shop';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import { AppHomeContent } from './pages/Home';

function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { setIsSearchOpen } = useSearch();
  const cartItems = useSelector((state: RootState) => state.cart.items);
  const cartItemCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SearchModal />
      {/* Header */}
      <header className="bg-white relative">
        <div className="max-w-7xl mx-auto">
          {/* Main header */}
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              {/* Left section - Logo */}
              <div className="flex items-center space-x-3">
                <Link to="/" className="flex items-center space-x-3">
                  <img 
                    src="/logo.svg" 
                    alt="Fits and Trends" 
                    className="h-12 w-12"
                  />
                  <span className="text-xl font-bold text-gray-900">Fits and Trends</span>
                </Link>
              </div>

              {/* Right section */}
              <div className="flex items-center space-x-4 sm:space-x-6">
                <button 
                  onClick={() => setIsSearchOpen(true)} 
                  className="text-gray-500 hover:text-gray-700"
                >
                  <Search className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
                <Link to="/cart" className="text-gray-500 hover:text-gray-700 relative">
                  <ShoppingBag className="h-5 w-5 sm:h-6 sm:w-6" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-purple-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {cartItemCount}
                    </span>
                  )}
                </Link>
                <button 
                  onClick={toggleMobileMenu}
                  className="text-gray-500 hover:text-gray-700 md:hidden"
                  aria-label="Toggle menu"
                >
                  {isMobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex justify-center space-x-8 py-4 border-t border-gray-200">
              <Link to="/" className="text-gray-600 hover:text-purple-600 flex items-center gap-2 transition-colors">
                <Home className="h-4 w-4" />
                Home
              </Link>
              <Link to="/men" className="text-gray-600 hover:text-purple-600 transition-colors">Men</Link>
              <Link to="/women" className="text-gray-600 hover:text-purple-600 transition-colors">Women</Link>
              <Link to="/cargo-pants" className="text-gray-600 hover:text-purple-600 transition-colors">Cargo Pants</Link>
              <Link to="/sale" className="text-gray-600 hover:text-purple-600 transition-colors">Sale</Link>
            </nav>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="md:hidden absolute top-16 left-0 right-0 bg-white border-t border-gray-200 z-50">
              <div className="px-4 py-2">
                <Link 
                  to="/" 
                  className="block py-3 text-gray-600 hover:text-gray-900 border-b border-gray-100"
                  onClick={toggleMobileMenu}
                >
                  Home
                </Link>
                <Link 
                  to="/men" 
                  className="block py-3 text-gray-600 hover:text-gray-900 border-b border-gray-100"
                  onClick={toggleMobileMenu}
                >
                  Men
                </Link>
                <Link 
                  to="/women" 
                  className="block py-3 text-gray-600 hover:text-gray-900 border-b border-gray-100"
                  onClick={toggleMobileMenu}
                >
                  Women
                </Link>
                <Link 
                  to="/cargo-pants" 
                  className="block py-3 text-gray-600 hover:text-gray-900 border-b border-gray-100"
                  onClick={toggleMobileMenu}
                >
                  Cargo Pants
                </Link>
                <Link 
                  to="/sale" 
                  className="block py-3 text-gray-600 hover:text-gray-900"
                  onClick={toggleMobileMenu}
                >
                  Sale
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>

      <Routes>
        <Route path="/men" element={<Men />} />
        <Route path="/women" element={<Women />} />
        <Route path="/cargo-pants" element={<CargoPants />} />
        <Route path="/sale" element={<Sale />} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/admin-dashboard" element={<AdminDashboard />} />
        <Route path="/" element={<AppHomeContent />} />
      </Routes>

      {/* Footer */}
      <footer className="bg-[#1F1235] text-gray-300 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Brand Section */}
          <div className="mb-12 text-center">
            <div className="flex items-center justify-center mb-4">
              <img src="/logo.svg" alt="Fits and Trends" className="h-16 w-16" />
              <h2 className="text-2xl font-bold text-white ml-3">Fits and Trends</h2>
            </div>
            <p className="text-gray-400 max-w-2xl mx-auto">
              MAKE HEADS TURN - The ultimate destination for premium footwear and fashion. 
              Offering the latest styles with unmatched comfort and quality.
            </p>
            <div className="flex justify-center space-x-6 mt-6">
              <a href="https://www.facebook.com/share/198DH3rkq8/?mibextid=wwXIfr" className="text-gray-400 hover:text-white transition-colors">
                <Facebook className="h-6 w-6" />
              </a>
              <a href="https://www.instagram.com/fits_andtrends?igsh=YzF0azVmbDJ4emFh&utm_source=qr" className="text-gray-400 hover:text-white transition-colors">
                <Instagram className="h-6 w-6" />
              </a>
              <a href="https://www.tiktok.com/@fitsandtrends?_t=ZM-8vUQMrLTMI9&_r=1" className="text-gray-400 hover:text-white transition-colors">
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 015.2-2.2V6.54a7.71 7.71 0 006.59 7.23.76.76 0 00.63-1.08z"/>
                </svg>
              </a>
              <a href="https://wa.me/2348104815958" className="text-gray-400 hover:text-white transition-colors">
                <MessageCircle className="h-6 w-6" />
              </a>
            </div>
          </div>

          {/* Navigation Sections */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div>
              <h3 className="text-white text-lg font-semibold mb-4">Shop</h3>
              <ul className="space-y-2">
                <li><Link to="/men" className="hover:text-white transition-colors">Men</Link></li>
                <li><Link to="/women" className="hover:text-white transition-colors">Women</Link></li>
                <li><Link to="/sale" className="hover:text-white transition-colors">Sale</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white text-lg font-semibold mb-4">Help</h3>
              <ul className="space-y-2">
                <li><Link to="/faqs" className="hover:text-white transition-colors">FAQs</Link></li>
                <li><Link to="/shipping" className="hover:text-white transition-colors">Shipping</Link></li>
                <li><Link to="/size-guide" className="hover:text-white transition-colors">Shoe Size Guide</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
              </ul>
            </div>
            <div className="col-span-2 md:col-span-2">
              <h3 className="text-white text-lg font-semibold mb-4">Stay Updated</h3>
              <p className="text-gray-400 mb-4">
                Subscribe to our newsletter for exclusive offers and updates.
              </p>
              <form className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 px-4 py-2 rounded-lg bg-[#2D1B47] border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Subscribe
                </button>
              </form>
              <div className="mt-4">
                <p className="text-gray-400">Call: <a href="tel:08104815958" className="hover:text-white transition-colors">08104815958</a></p>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="border-t border-gray-700 pt-8 text-center">
            <p className="text-gray-400">© 2025 FitsAndTrends. All rights reserved.</p>
            <div className="flex justify-center space-x-4 mt-4">
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;